import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectPersonFromMembers } from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.selectors'
import { ButtonDesigns } from 'src/components/Button/Button'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import { OrganizationRoles } from 'src/enums'
import Trns from 'src/localization/Trns'
import { peopleMembersDetailUrl } from 'src/navigation/urls'
import { createSetIsOrgOwnerReassignmentPopupOpen } from 'src/redux/actions/app.actions'
import { putOrganizationRole } from 'src/redux/actions/data/organizations.actions'
import { selectIsOrgOwnerReassignmentFromState } from 'src/redux/selectors/app.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { css } from 'styled-components'

const OrgOwnerReassignmentPopup: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch()
  const person = useSelector(selectPersonFromMembers)
  const orgOwnerReassignment = useSelector(
    selectIsOrgOwnerReassignmentFromState,
  )
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { analytics } = useAnalyticsContext()

  const userHasNoName =
    person?.firstName === undefined && person?.lastName === undefined

  const reassignOwner = (): void => {
    if (person && currentOrganization && person.firstName && person.lastName) {
      dispatch(
        putOrganizationRole(
          currentOrganization?.id,
          person.id,
          person.firstName,
          person.lastName,
          <Trns source={'Owner'} />,
          false,
          { role: OrganizationRoles.owner },
        ),
      )
      analytics?.track('user detail - update user role', {
        screen: peopleMembersDetailUrl(person.id),
        interaction: 'button press',
        intent: 'update user role',
        previous_role: person.organizationRole,
        new_role: OrganizationRoles.owner,
      })
      dispatch(createSetIsOrgOwnerReassignmentPopupOpen(false))
    }
  }

  const closePopup = (): void => {
    dispatch(createSetIsOrgOwnerReassignmentPopupOpen(false))
  }

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <ConfirmationPopupContent
        title={<Trns source={'Confirm Org Owner reassignment'} />}
        subtitle={
          <Trns
            source={
              'Are you sure you want to assign ${nameAndEmail} as the new organization owner? As the new org owner, they cannot be removed from your organization, and have full control over the organization and its members. Your role will be switched to Admin.'
            }
            expressions={{
              nameAndEmail: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {userHasNoName
                    ? `${person?.email}`
                    : `${person?.firstName} ${person?.lastName} (${person?.email})`}
                </span>
              ),
            }}
          />
        }
        actionButtonLabel={<Trns source={'Yes, reassign Org Owner'} />}
        actionButtonOnClick={reassignOwner}
        actionButtonLoading={orgOwnerReassignment}
        actionButtonDesign={ButtonDesigns.designRed}
        cancelButtonLabel={<Trns source="Cancel" />}
        cancelButtonOnClick={closePopup}
        isCancelButtonLink={true}
      />
    </Popup>
  )
}

export default OrgOwnerReassignmentPopup
